import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd } from '@angular/router';
import { FooterComponent } from './components/footer/footer.component';
import { PreloaderComponent } from './components/preloader/preloader.component';
import { LoadingService } from './services/loading.service';
import { AnalyticsService } from './services/analytics.service';
import { AuthService } from './services/auth.service';
import { CommonModule } from '@angular/common';
import { FontSizeService } from './services/font-size.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: true,
  imports: [RouterOutlet, FooterComponent, PreloaderComponent, CommonModule],
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'mindmapsforupsc';
  isLoading = true;
  showDebugButton = false;
  private fontSizeSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;
  private authSubscription: Subscription | null = null;

  constructor(
    private loadingService: LoadingService,
    private fontSizeService: FontSizeService,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Show debug button only on localhost
    this.showDebugButton = window.location.hostname === 'localhost' || 
                          window.location.hostname === '127.0.0.1';

    // Initialize Google Analytics 4
    this.analyticsService.initializeGA4(environment.ga4MeasurementId);
    
    // Wait a moment for GA4 to initialize, then track page view
    setTimeout(() => {
      console.log('✅ GA4 initialization timeout completed');
      
      // Track page view when app loads
      this.analyticsService.trackPageView('Home', '/');
      
      // Test anonymous user tracking immediately
      console.log('🧪 Testing anonymous user tracking...');
      this.analyticsService.trackAnonymousUserAction('app_loaded', {
        page: 'home',
        timestamp: new Date().toISOString()
      });
    }, 1000);

    // Add test method accessible from browser console
    (window as any).testGA4 = () => {
      console.log('🧪 Manual GA4 Test Started');
      this.testGA4Events();
    };

    // Add anonymous filtering test to browser console
    (window as any).testAnonymousFiltering = () => {
      console.log('🔍 Testing Anonymous User Filtering');
      this.testAnonymousFiltering();
    };

    // Subscribe to loading status
    this.loadingService.appLoading$.subscribe(loading => {
      this.isLoading = loading;
      
      // Apply blur effect to main content when loading
      const mainContent = document.querySelector('router-outlet')?.parentElement;
      if (mainContent) {
        if (loading) {
          mainContent.classList.add('content-blurred');
        } else {
          mainContent.classList.remove('content-blurred');
        }
      }
    });

    // Initialize font size service
    this.fontSizeSubscription = this.fontSizeService.fontSize$.subscribe(size => {
      console.log(`App component: Font size changed to ${size}`);
    });

    // Track page views on route changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Track page view
        const pageTitle = this.getPageTitle(event.urlAfterRedirects);
        this.analyticsService.trackPageView(pageTitle, event.urlAfterRedirects);
      });

    // Track authentication state changes
    this.authSubscription = this.authService.authState$.subscribe(isLoggedIn => {
      if (isLoggedIn) {
        const user = this.authService.getUserInfo();
        if (user) {
          this.analyticsService.setUserProperties(user);
        }
      } else {
        // Track anonymous user behavior
        this.analyticsService.trackAnonymousUserAction('browsing_while_logged_out');
      }
    });

    // Note: Initial page view is now handled automatically by AnalyticsService
    // after GA4 is properly loaded to prevent race conditions
  }

  ngOnDestroy(): void {
    if (this.fontSizeSubscription) {
      this.fontSizeSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }

    // End user session when app is destroyed
    this.analyticsService.endUserSession();
  }

  private getPageTitle(url: string): string {
    // Map URLs to meaningful page titles
    if (url === '/' || url === '/home') return 'Home';
    if (url.includes('/login')) return 'Login';
    if (url.includes('/dashboard')) return 'Dashboard';
    if (url.includes('/mindmap')) return 'Mind Map';
    if (url.includes('/quiz')) return 'Quiz';
    if (url.includes('/upload')) return 'File Upload';
    if (url.includes('/profile')) return 'Profile';
    
    // Default to the URL path as title
    return url.split('/').pop() || 'Unknown Page';
  }

  // Test method for debugging anonymous events
  testAnonymousEvent(): void {
    console.log('🧪 Manual test: Triggering anonymous user event');
    this.analyticsService.trackAnonymousUserAction('manual_test', {
      test_type: 'button_click',
      timestamp: new Date().toISOString(),
      page: window.location.pathname
    });
  }

  // NEW: Test method to manually trigger events
  testGA4Events(): void {
    console.group('🧪 GA4 Manual Test');
    
    // Test 1: Anonymous user action
    console.log('📤 Test 1: Anonymous user action');
    this.analyticsService.trackAnonymousUserAction('manual_test', {
      test_type: 'manual',
      timestamp: new Date().toISOString()
    });
    
    // Test 2: Page view
    console.log('📤 Test 2: Page view');
    this.analyticsService.trackPageView('Test Page', '/test');
    
    // Test 3: Simulate logged-in user to create User ID data
    console.log('📤 Test 3: Simulating logged-in user');
    this.simulateLoggedInUser();
    
    // Test 4: Direct gtag call
    console.log('📤 Test 4: Direct gtag call');
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('event', 'direct_test_event', {
        event_category: 'test',
        event_label: 'direct_call',
        value: 1
      });
      console.log('✅ Direct gtag call completed');
    } else {
      console.error('❌ gtag function not available');
    }
    
    console.groupEnd();
  }

  // NEW: Simulate logged-in user to generate User ID data
  simulateLoggedInUser(): void {
    console.log('👤 Simulating logged-in user for User ID dimension...');
    
    // Simulate setting a User ID
    const testUserId = 'test_user_' + Date.now();
    
    if (typeof (window as any).gtag === 'function') {
      // Set User ID
      (window as any).gtag('config', 'G-RE9JSRWRDW', {
        user_id: testUserId
      });
      
      // Track login event
      (window as any).gtag('event', 'login', {
        method: 'test',
        user_id: testUserId,
        user_status: 'logged_in'
      });
      
      // Track some activity as logged-in user
      (window as any).gtag('event', 'page_view', {
        page_title: 'Test Page - Logged In',
        user_id: testUserId,
        user_status: 'logged_in'
      });
      
      console.log('✅ Simulated logged-in user with ID:', testUserId);
      
      // Clear User ID after test (set to NULL for anonymous)
      setTimeout(() => {
        (window as any).gtag('config', 'G-RE9JSRWRDW', {
          user_id: "NULL"  // Set to NULL string for anonymous filtering
        });
        console.log('🧹 Set User ID to NULL for anonymous user');
      }, 5000);
    }
  }

  // NEW: Test anonymous user filtering
  testAnonymousFiltering(): void {
    console.group('🔍 Testing Anonymous User Filtering');
    
    // Ensure we're in anonymous state
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('config', 'G-RE9JSRWRDW', {
        user_id: "NULL"
      });
      
      console.log('🎯 Set User ID to NULL for anonymous user');
      console.log('📊 Now all events will have User ID = "NULL"');
      
      // Track some anonymous activity
      this.analyticsService.trackAnonymousUserAction('filter_test', {
        test_purpose: 'anonymous_filtering',
        timestamp: new Date().toISOString()
      });
      
      console.log('✅ Anonymous events sent with User ID = NULL');
      console.log('🔧 In GA4, filter: "Signed in with user ID contains NULL"');
    }
    
    console.groupEnd();
  }
}
